import React, { createContext, useContext, useState, useEffect } from 'react';
import SystemAlertModal from '../components/SystemAlertModal';
import useSystemAlerts from '../hooks/useSystemAlerts';

const SystemAlertContext = createContext({});

/**
 * SystemAlertProvider - Manages system-wide alerts from the backend
 * Automatically fetches alerts on app load and polls for updates
 */
export const SystemAlertProvider = ({ children }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Fetch alerts with 5-minute polling interval
  const {
    alerts,
    loading,
    error,
    refetch
  } = useSystemAlerts(5 * 60 * 1000, true);

  /**
   * Automatically show modal when new alerts are detected
   */
  useEffect(() => {
    if (!loading && alerts.length > 0 && !isMinimized) {
      setModalOpen(true);
    }
  }, [alerts, loading, isMinimized]);

  /**
   * Update minimized state when alerts change
   */
  useEffect(() => {
    if (alerts.length === 0) {
      setIsMinimized(false);
      setModalOpen(false);
    }
  }, [alerts]);

  /**
   * Minimize modal - hide it but keep showing notification indicator
   */
  const handleMinimize = () => {
    setModalOpen(false);
    setIsMinimized(true);
  };

  /**
   * Manually trigger alert check
   */
  const checkForAlerts = () => {
    refetch();
  };

  /**
   * Manually show the alert modal (used by navbar notification icon)
   */
  const showAlertModal = () => {
    setModalOpen(true);
    setIsMinimized(false);
  };

  /**
   * Toggle modal visibility
   */
  const toggleAlertModal = () => {
    if (modalOpen) {
      handleMinimize();
    } else {
      showAlertModal();
    }
  };

  /**
   * Context value provided to children
   */
  const contextValue = {
    alerts,
    loading,
    error,
    hasAlerts: alerts.length > 0,
    isMinimized,
    modalOpen,
    checkForAlerts,
    showAlertModal,
    toggleAlertModal
  };

  return (
    <SystemAlertContext.Provider value={contextValue}>
      {children}

      {/* Global System Alert Modal */}
      <SystemAlertModal
        open={modalOpen}
        alerts={alerts}
        onMinimize={handleMinimize}
      />
    </SystemAlertContext.Provider>
  );
};

/**
 * Hook to access system alert context
 */
export const useSystemAlertContext = () => {
  const context = useContext(SystemAlertContext);

  if (!context) {
    throw new Error('useSystemAlertContext must be used within SystemAlertProvider');
  }

  return context;
};

export default SystemAlertContext;
